import {
  LockFilled,
  MailFilled,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Alert, Divider, Form, Input, Modal, Typography } from "antd";
import freeEmailDomains from 'free-email-domains';
import { FunctionComponent, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce/lib";
import { useStateIfMounted } from "use-state-if-mounted";
import isEmail from "validator/lib/isEmail";
import ActionBtn from "../../shared/action-btn";
import { ROUTE_PUBLIC_SIGN_IN } from "../../shared/app-constants";
import getErrorMessage from "../../shared/get-error-msg";
import getPublicRoute from "../../shared/get-public-route";
import { LocalStorageManager } from "../../shared/local-storage-manager";
import { SignUpData } from "../../shared/sign-up-data";
import { AppContext } from "../contexts/app-context";
import HttpClient from "../http-client";

interface SignUpPageProps { }

const organizationCheckCache: any = {};

const SignUpPage: FunctionComponent<SignUpPageProps> = () => {
  const [busy, setBusy] = useStateIfMounted(false);
  const [organizationExists, setOrganizationExists] = useStateIfMounted(true);
  const [serverMsg, setServerMsg] = useStateIfMounted<string | undefined>(
    undefined
  );
  const { setUser } = useContext(AppContext);
  const [form] = Form.useForm();

  const [serverMsgType, setServerMsgType] = useStateIfMounted<
    "error" | "success" | undefined
  >(undefined);
  const navigate = useNavigate();

  const checkOrganization = useDebouncedCallback(async (email: string) => {
    if (!email) return;
    if (typeof organizationCheckCache[email] !== "undefined") {
      setOrganizationExists(organizationCheckCache[email]);
      return;
    }
    if (isEmail(email)) {
      const upn = email.split("@").pop()?.toLowerCase();
      if (upn && freeEmailDomains.includes(upn)) {
        Modal.error({
          title: "Organization email required!",
          content: "This tool is only for organizations! Please use your organization email!",
        });
        setOrganizationExists(true);
        return;
      }
      setBusy(true);
      const { data } = await HttpClient.get(
        `organization/check?upn=${encodeURIComponent(email.split("@").pop()!)}`
      );

      organizationCheckCache[email] = !!data?.data?.exists;
      setOrganizationExists(!!data?.data?.exists);
      setBusy(false);
    }
  }, 100);
  const register = async (signUpData: SignUpData) => {
    setBusy(true);
    setServerMsg(undefined);
    try {
      const { data } = await HttpClient.post("user/sign-up", signUpData);

      if (data?.data?.access_token) {
        const { access_token, refresh_token } = data.data;
        setServerMsgType("success");
        setServerMsg("You are successfully logged in!");
        LocalStorageManager.saveTokens(access_token, refresh_token);
        setUser(data.data);
      } else {
        throw new Error("Login failed!");
      }
    } catch (error: any) {
      if (error?.response?.data?.error) {
        setServerMsgType("error");
        setServerMsg(getErrorMessage(error?.response?.data));
      }
    }
    setBusy(false);
  };
  return (
    <div className="form-container">
      <Form
        form={form}
        onFinish={register}
        onValuesChange={(_, val) => checkOrganization(val.email)}
        className="public-form"
      >
        <Typography.Title level={4} className="title">
          Registration
        </Typography.Title>
        {serverMsg && (
          <Alert
            type={serverMsgType}
            message={serverMsg}
            style={{ marginBottom: 10 }}
          />
        )}
        <Form.Item
          name="name"
          rules={[{ required: true, message: "Name is required!" }]}
        >
          <Input suffix={<UserOutlined />} placeholder="Your name!" />
        </Form.Item>
        <Form.Item
          name="email"
          rules={[
            { required: true, type: "email", message: "Email is required!" },
            () => ({
              validator(_, value) {
                console.log(value);
                const upn = value?.split("@").pop()?.toLowerCase();
                if (upn && freeEmailDomains.includes(upn)) {
                  return Promise.reject(
                    new Error("Please use your organization email!")
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input
            type={"email"}
            suffix={<MailFilled />}
            placeholder="Your organization email!"
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[{ required: true, min: 6, message: "Password is required!" }]}
        >
          <Input
            suffix={<LockFilled />}
            type="password"
            placeholder="Your password"
          />
        </Form.Item>
        {!organizationExists && (
          <Form.Item
            name="organization_name"
            rules={[
              { required: true, message: "Organization name is required!" },
            ]}
          >
            <Input
              suffix={<TeamOutlined />}
              placeholder="Your organization name!"
            />
          </Form.Item>
        )}
        <Form.Item>
          <ActionBtn
            busy={busy}
            type="primary"
            htmlType="submit"
            className="button"
          >
            Register
          </ActionBtn>
        </Form.Item>
        <Divider orientation="center">Or</Divider>
        <div className="tc">
          <Link to={{ pathname: getPublicRoute({}, ROUTE_PUBLIC_SIGN_IN) }}>
            Login!
          </Link>
        </div>
      </Form>
    </div>
  );
};

export default SignUpPage;
